import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Loading, Tabs } from "@/deps/ui-library";
import { useHelpStatus } from "@/api/queries";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";

type Section = "guides" | "tickets";

const SECTION_PATH: Record<Section, "/help" | "/help/tickets"> = {
  guides: "/help",
  tickets: "/help/tickets",
};

/**
 * The frame every Help screen sits in, and the gate in front of it.
 *
 * The gate is the API's answer. `help-status` says off when `PlatformSupport:HelpEnabled` is false
 * or no support host is configured, and then there is nothing behind these routes — a bookmarked
 * link lands on this explanation rather than on a page of failures.
 */
export function HelpGate({ section, children }: { section: Section; children: ReactNode }) {
  const { data, isPending } = useHelpStatus();

  if (isPending) return <Loading.Spinner size="sm" />;

  if (!data?.enabled) {
    return (
      <EmptyState
        icon="LifeBuoy"
        title="Help is not switched on"
        description="When Rigo turns on the help centre for Rigo Accounting, its guides and your organisation's support tickets appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Help"
        subtitle="Guides for Rigo Accounting, and your organisation's tickets with Rigo's support team"
        actions={
          // The selected tab is the route, so the tabs never hold state of their own: `value` is
          // the section this screen belongs to. Each tab is the router's own `Link` — a real
          // `<a href>` a person can copy or open in a new tab, and a plain click is the router's,
          // so it never reloads the page. `Tabs.Root` stays controlled because the router, not the
          // tab strip, decides which section is open.
          <Tabs.Root value={section} activationMode="manual" className="w-auto">
            <Tabs.List variant="pills" aria-label="Help sections">
              <Tabs.LinkTrigger asChild value="guides" variant="pills">
                <Link to={SECTION_PATH.guides}>Guides</Link>
              </Tabs.LinkTrigger>
              {data.canRaiseTickets ? (
                <Tabs.LinkTrigger asChild value="tickets" variant="pills">
                  <Link to={SECTION_PATH.tickets}>My tickets</Link>
                </Tabs.LinkTrigger>
              ) : null}
              <Tabs.Indicator variant="pills" />
            </Tabs.List>
          </Tabs.Root>
        }
      />
      {children}
    </div>
  );
}
